import React, { useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Animated,
  Easing,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { router, usePathname, type Href } from 'expo-router';
import { LogOut, Monitor, Sun, Moon, Compass, Bell, type LucideIcon } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  FONT_SIZES,
  withWeight,
  ELEVATION,
  SIDEBAR_FOOTER_HEIGHT,
  SIDEBAR_RAIL_WIDTH,
  TYPOGRAPHY,
} from '../../theme';
import { Avatar, Button, useConfirm } from '../common';
import { useAuthStore } from '../../store/authStore';
import { useAuthGateStore } from '../../store/authGateStore';
import { useNotificationsStore } from '../../store/notificationsStore';
import { useOutboxStore } from '../../store/outboxStore';
import { useUIStore } from '../../store/uiStore';
import { useThemeStore, type ThemeMode } from '../../store/themeStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { appAlert } from '../../utils/appAlert';
import { UpgradeAccountSheet } from '../auth/UpgradeAccountSheet';

const APPEARANCE_OPTIONS: { id: ThemeMode; icon: LucideIcon; label: string }[] = [
  { id: 'light', icon: Sun, label: 'Light' },
  { id: 'dark', icon: Moon, label: 'Dark' },
  { id: 'system', icon: Monitor, label: 'System' },
];

const DROPDOWN_WIDTH = 280;
const HEADER_HEIGHT = 44;

/**
 * Anchored dropdown shown when the header avatar is tapped. Renders at the
 * root layout (alongside the other global sheets) so it floats over any
 * screen.
 *
 * Signed-up: identity row → /profile, Tour REDI (with unseen dot),
 * Appearance, Sign Out.
 * Anon: identity (disabled) → Save your account → Sign in to existing →
 * Tour REDI → Appearance. No Sign Out.
 *
 * Open/close is driven by the shared single-sheet contract in uiStore
 * (`activeSheet === 'userMenu'`), so Header's avatar ring + the auto-close
 * behaviour when another sheet opens both keep working unchanged.
 */
export function UserMenuDropdown() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user, signOut } = useAuthStore();
  const confirm = useConfirm();
  const activeSheet = useUIStore((s) => s.activeSheet);
  const open = activeSheet === 'userMenu';
  const sheetOpenTick = useUIStore((s) => s.sheetOpenTick);
  const closeSheet = useUIStore((s) => s.closeSheet);
  const openSheet = useUIStore((s) => s.openSheet);
  const hasSeenTour = useUIStore((s) => s.hasSeenTour);
  const anchor = useUIStore((s) => s.userMenuAnchor);
  const { mode, setMode } = useThemeStore();
  // Desktop shell: the trigger lives in the sidebar footer (bottom-left) on
  // every screen except the map route, where it's the rail's bottom identity
  // block instead ('rail') — either way the menu anchors by the trigger,
  // not under the top-right header avatar mobile uses.
  const isDesktopShell = useDesktopShell();

  const progress = useRef(new Animated.Value(0)).current;

  // The dropdown doesn't block the tab bar / sidebar, so the user can
  // navigate while it's open — close it when the route changes underneath,
  // or it stays painted over the next screen. (Menu rows that navigate call
  // closeSheet() themselves before pushing; this covers every other exit.)
  const pathname = usePathname();
  const prevPathname = useRef(pathname);
  useEffect(() => {
    if (prevPathname.current === pathname) return;
    prevPathname.current = pathname;
    if (useUIStore.getState().activeSheet === 'userMenu') closeSheet();
  }, [pathname, closeSheet]);

  // Note: unlike the prior bottom-sheet incarnation, we don't hideTabBar()
  // here. A small top-right dropdown doesn't conflict with the floating tab
  // bar, and on the map screen the avatar itself is gated by tabBarVisible
  // (app/(tabs)/index.tsx) — hiding it would make the trigger disappear.
  useEffect(() => {
    if (open) {
      Animated.timing(progress, {
        toValue: 1,
        duration: 140,
        easing: Easing.out(Easing.quad),
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    } else {
      progress.setValue(0);
    }
    // sheetOpenTick is in deps so re-opening from a stuck state still
    // re-runs the open path (matches UserMenuSheet's pattern).
  }, [open, sheetOpenTick, progress]);

  const navigate = useCallback(
    (path: string) => {
      closeSheet();
      router.push(path as Href);
    },
    [closeSheet]
  );

  const handleTour = useCallback(() => {
    // openSheet auto-closes 'userMenu' (single-active-sheet contract). The
    // tour-seen flag is flipped by HowReDIWorksSheet on dismiss.
    openSheet('howItWorks');
  }, [openSheet]);

  // Alerts lives here since it left the tab bar / sidebar (Aug 2026); the
  // unread count rides this row and lights the avatar dot outside.
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const handleAlerts = useCallback(() => navigate('/(tabs)/alerts'), [navigate]);

  const handleSaveAccount = useCallback(() => {
    openSheet('upgradeAccount');
  }, [openSheet]);

  const handleSignInExisting = useCallback(async () => {
    closeSheet();
    try {
      await signOut();
    } catch {
      // Continue regardless — even a partial sign-out lets the gate render.
    }
    useAuthGateStore
      .getState()
      .open(() => {}, { message: 'Sign in to your existing REDI account' });
  }, [closeSheet, signOut]);

  const handleSignOut = useCallback(async () => {
    // Items are per-uid and durable (see docs/plans — Durable Offline
    // Outbox), so nothing is lost either way — this is a heads-up, not a
    // data-loss warning.
    const pendingCount = useOutboxStore.getState().pendingCount;
    if (pendingCount > 0) {
      const confirmed = await confirm({
        title: 'Sign out?',
        message: `You have ${pendingCount} observation${pendingCount === 1 ? '' : 's'} waiting to upload. They will upload next time you sign in. Sign out anyway?`,
        confirmLabel: 'Sign Out',
        destructive: true,
      });
      if (!confirmed) return;
    }
    closeSheet();
    try {
      await signOut();
    } catch {
      appAlert('Error', 'Failed to sign out');
    }
  }, [closeSheet, signOut, confirm]);

  if (!user) {
    return <UpgradeAccountSheet visible={activeSheet === 'upgradeAccount'} onClose={closeSheet} />;
  }

  const isAnon = user.isAnonymous === true;
  const fullName = isAnon
    ? 'Fire Scout'
    : `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'User';
  const subtitle = isAnon ? 'Your posts show as Fire Scout (anonymous)' : user.email;

  const translateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [-6, 0],
  });

  return (
    <>
      {open && (
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={closeSheet}
          // accessible={false}: a labeled Pressable wrapping the dropdown
          // collapses every menu row into one opaque element on iOS
          // (VoiceOver + UI tests can't reach the rows).
          accessible={false}
          accessibilityLabel="Close user menu">
          <Animated.View
            pointerEvents="box-none"
            style={[
              styles.dropdownWrapper,
              // Four hard-coded anchors below — a candidate for
              // useAnchoredPosition (src/hooks/useAnchoredPosition.ts)
              // eventually, left alone for now (gradual adoption).
              isDesktopShell
                ? // Sept 2026 A2: the map route's avatar trigger sits at the
                  // bottom of the rail, under the map tools tray. Opens to
                  // the right of the rail, its bottom edge level with the
                  // avatar's, instead of the usual bottom-left footer
                  // position — every other desktop screen still anchors
                  // there regardless of what `anchor` says (they never pass
                  // 'rail').
                  anchor === 'rail'
                  ? {
                      bottom: SPACING.md,
                      left: SIDEBAR_RAIL_WIDTH + SPACING.xs,
                    }
                  : { bottom: SIDEBAR_FOOTER_HEIGHT, left: SPACING.sm }
                : anchor === 'mapCorner'
                  ? // Mobile map: the avatar trigger sits in the bottom-right
                    // stack above MapControls (app/(tabs)/index.tsx,
                    // bottom: 240 + insets.bottom, 36px tall). Open the menu
                    // directly above it so it reads as attached.
                    { bottom: insets.bottom + 240 + 36 + SPACING.sm, right: SPACING.md }
                  : {
                      top: insets.top + HEADER_HEIGHT + 12,
                      right: SPACING.md,
                    },
              {
                opacity: progress,
                transform: [{ translateY }],
              },
            ]}>
            {/* Caret only when hanging from the header avatar — the other
                anchors open away from their trigger edge. */}
            {!isDesktopShell && anchor === 'header' && (
              <>
                <View style={[styles.caretBorder, { borderBottomColor: colors.border }]} />
                <View style={[styles.caret, { borderBottomColor: colors.background }]} />
              </>
            )}
            <Pressable
              // Swallow taps inside the dropdown so the outer backdrop
              // Pressable doesn't close it. accessible={false} so the rows
              // stay individually reachable (Pressable defaults to true).
              onPress={() => {}}
              accessible={false}
              testID="user-menu-dropdown"
              style={[
                styles.dropdown,
                ELEVATION.lg,
                {
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                },
              ]}>
              <TouchableOpacity
                style={styles.identity}
                onPress={isAnon ? undefined : () => navigate('/profile')}
                disabled={isAnon}
                accessibilityRole={isAnon ? undefined : 'button'}
                accessibilityLabel={isAnon ? undefined : `Go to profile for ${fullName}`}
                activeOpacity={0.7}>
                <Avatar
                  size={40}
                  name={isAnon ? '' : fullName}
                  imageUrl={isAnon ? null : user.avatarUrl}
                  anonymous={isAnon}
                />
                <View style={styles.identityText}>
                  <Text style={[styles.name, { color: colors.text }]} numberOfLines={1}>
                    {fullName}
                  </Text>
                  {subtitle ? (
                    <Text
                      style={[styles.subtitle, { color: colors.textSecondary }]}
                      numberOfLines={1}>
                      {subtitle}
                    </Text>
                  ) : null}
                </View>
              </TouchableOpacity>

              {isAnon && (
                <View style={styles.savePrompt}>
                  <Button
                    title="Save your account"
                    onPress={handleSaveAccount}
                    variant="primary"
                    fullWidth
                    testID="user-menu-save-account"
                  />
                  <Text style={[styles.saveHelper, { color: colors.textSecondary }]}>
                    Keep your posts if you switch devices or sign out.
                  </Text>
                  <TouchableOpacity
                    onPress={handleSignInExisting}
                    accessibilityRole="button"
                    accessibilityLabel="Sign in to existing account"
                    hitSlop={8}>
                    <Text style={[styles.signInExistingLink, { color: colors.primary }]}>
                      Sign in to existing account
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              <View style={[styles.divider, { backgroundColor: colors.border }]} />

              <TouchableOpacity
                style={styles.row}
                onPress={handleAlerts}
                accessibilityRole="button"
                accessibilityLabel="Alerts"
                testID="user-menu-alerts"
                activeOpacity={0.7}>
                <Bell size={18} color={colors.textSecondary} />
                <Text style={[styles.rowLabel, { color: colors.text }]}>Alerts</Text>
                {unreadCount > 0 && (
                  <View style={[styles.unreadPill, { backgroundColor: colors.dangerStrong }]}>
                    <Text style={[styles.unreadPillText, { color: colors.white }]}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.row}
                onPress={handleTour}
                accessibilityRole="button"
                accessibilityLabel="Tour REDI"
                testID="user-menu-tour"
                activeOpacity={0.7}>
                <Compass size={18} color={colors.textSecondary} />
                <Text style={[styles.rowLabel, { color: colors.text }]}>Tour REDI</Text>
                {!hasSeenTour && (
                  <View style={styles.rowDotWrap}>
                    <View style={[styles.rowDot, { backgroundColor: colors.primary }]} />
                  </View>
                )}
              </TouchableOpacity>

              <View style={[styles.divider, { backgroundColor: colors.border }]} />

              <View style={styles.appearanceRow}>
                <Text style={[styles.appearanceLabel, { color: colors.textSecondary }]}>
                  Appearance
                </Text>
                <View style={[styles.segmented, { backgroundColor: colors.surface }]}>
                  {APPEARANCE_OPTIONS.map((opt) => {
                    const selected = mode === opt.id;
                    const Icon = opt.icon;
                    return (
                      <TouchableOpacity
                        key={opt.id}
                        accessibilityRole="button"
                        accessibilityLabel={opt.label}
                        accessibilityState={{ selected }}
                        style={[
                          styles.segment,
                          selected && { backgroundColor: colors.primarySoft },
                        ]}
                        onPress={() => setMode(opt.id)}
                        activeOpacity={0.7}>
                        <Icon size={16} color={selected ? colors.primary : colors.textSecondary} />
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              {!isAnon && (
                <>
                  <View style={[styles.divider, { backgroundColor: colors.border }]} />
                  <TouchableOpacity
                    style={styles.row}
                    onPress={handleSignOut}
                    accessibilityRole="button"
                    accessibilityLabel="Sign out"
                    activeOpacity={0.7}>
                    <LogOut size={18} color={colors.danger} />
                    <Text style={[styles.rowLabel, { color: colors.danger }]}>Sign Out</Text>
                  </TouchableOpacity>
                </>
              )}
            </Pressable>
          </Animated.View>
        </Pressable>
      )}

      <UpgradeAccountSheet visible={activeSheet === 'upgradeAccount'} onClose={closeSheet} />
    </>
  );
}

const styles = StyleSheet.create({
  dropdownWrapper: {
    position: 'absolute',
    width: DROPDOWN_WIDTH,
  },
  caretBorder: {
    position: 'absolute',
    top: -6,
    right: 10,
    width: 0,
    height: 0,
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderBottomWidth: 7,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    zIndex: 1,
  },
  caret: {
    position: 'absolute',
    top: -5,
    right: 11,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    zIndex: 2,
  },
  dropdown: {
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
  },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.sm,
    gap: SPACING.sm,
  },
  identityText: {
    flex: 1,
  },
  name: {
    ...TYPOGRAPHY.button,
    fontSize: FONT_SIZES.md,
  },
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  savePrompt: {
    gap: SPACING.xs,
    paddingBottom: SPACING.xs,
  },
  saveHelper: {
    ...TYPOGRAPHY.caption,
    textAlign: 'center',
  },
  signInExistingLink: {
    ...TYPOGRAPHY.label,
    textAlign: 'center',
    paddingVertical: SPACING.xs,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: SPACING.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    gap: SPACING.sm,
  },
  rowLabel: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
    fontSize: FONT_SIZES.md,
    flex: 1,
  },
  rowDotWrap: {
    width: 12,
    height: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  unreadPill: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: SPACING.xs,
  },
  unreadPillText: {
    ...TYPOGRAPHY.caption,
    fontFamily: TYPOGRAPHY.h4.fontFamily,
  },
  appearanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: SPACING.sm,
  },
  appearanceLabel: {
    ...TYPOGRAPHY.label,
  },
  segmented: {
    flexDirection: 'row',
    borderRadius: BORDER_RADIUS.md,
    padding: 2,
    gap: 2,
  },
  segment: {
    width: 36,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: BORDER_RADIUS.sm,
  },
});
